import { Component, Input, OnInit } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { GlobalService } from 'src/app/api/global.service';
import { CustomFieldDef, CustomFieldsService } from 'src/app/api/custom-fields.service';

// Shared by both the contacts and companies "Manage Firm" settings cards - entity_type only
// changes the wording shown, the editing/saving logic is identical either way.
@Component({
  selector: 'app-manage-custom-fields',
  templateUrl: './manage.component.html',
  styleUrls: ['./manage.component.scss'],
})
export class ManageComponent implements OnInit {

  @Input() entity_type: 'contacts' | 'companies' = 'contacts';

  // _original_type = the type the field had when the manager opened, used to warn on a type change (never saved)
  fields: (CustomFieldDef & { _original_type?: CustomFieldDef['type'] })[] = [];
  isLoading = true;
  isSaving = false;
  // Names of the global fields when the manager opened - any missing at save time were deleted
  private original_names: string[] = [];

  constructor(
    private modalController: ModalController,
    private alertController: AlertController,
    public globalService: GlobalService,
    private customFieldsService: CustomFieldsService
  ) { }

  async ngOnInit() {
    const defs = await this.customFieldsService.GetFieldDefs();
    this.fields = JSON.parse(JSON.stringify(defs[this.entity_type] || []));
    this.fields.forEach(f => f._original_type = f.type);
    this.original_names = this.fields.map(f => f.name);
    this.isLoading = false;
  }

  get entityLabel(): string {
    return this.entity_type === 'contacts' ? 'Contact' : 'Company';
  }

  AddField() {
    this.fields.push({ name: '', type: 'text' });
  }

  RemoveField(index: number) {
    this.fields.splice(index, 1);
  }

  ValidationError(): string {
    if (this.fields.some(f => !f.name || !f.name.trim())) return 'Every field needs a name.';
    const names = this.fields.map(f => f.name.trim().toLowerCase());
    if (new Set(names).size !== names.length) return 'Field names must be unique.';
    return '';
  }

  async Save() {
    const error = this.ValidationError();
    if (error) {
      this.globalService.presentToastMessage(error, false);
      return;
    }
    const changed = this.fields.filter(f => f._original_type && f._original_type !== f.type);
    if (changed.length && !(await this.ConfirmTypeChange(changed))) return;

    const live_names = new Set(this.fields.map(f => f.name));
    const removed = this.original_names.filter(name => !live_names.has(name));
    if (removed.length && !(await this.ConfirmRemoval(removed))) return;

    this.isSaving = true;
    this.customFieldsService.AssignFieldIds(this.fields);
    const result = await this.customFieldsService.SaveFieldDefs(this.entity_type, this.fields.map(({ _original_type, ...field }) => field), removed);
    this.isSaving = false;
    this.globalService.presentToastMessage(result.message || (result.success ? 'Saved' : 'Failed to save'), result.success);
    if (result.success) this.modalController.dismiss({ saved: true });
  }

  // Changing a global field's type re-types it on every contact/company that has it the next time that
  // record is opened, and values that don't fit the new type are cleared.
  private async ConfirmTypeChange(changed: any[]): Promise<boolean> {
    const names = changed.map(f => `<strong>${this.Escape(f.name)}</strong>`).join(', ');
    return new Promise<boolean>(async (resolve) => {
      const alert = await this.alertController.create({
        header: 'Change Field Type?',
        message: `You are changing the type of ${names}. Changing the type may cause a loss of data for any ${this.entityLabel.toLowerCase()} whose previously entered value does not match the new type.`,
        backdropDismiss: false,
        buttons: [
          { text: 'Cancel', role: 'cancel', handler: () => resolve(false) },
          { text: 'Change Type', role: 'destructive', handler: () => resolve(true) }
        ]
      });
      await alert.present();
    });
  }

  // Deleting (or renaming) a global field removes it, and every value entered for it, from all contacts/companies.
  private async ConfirmRemoval(removed: string[]): Promise<boolean> {
    const names = removed.map(name => `<strong>${this.Escape(name)}</strong>`).join(', ');
    return new Promise<boolean>(async (resolve) => {
      const alert = await this.alertController.create({
        header: 'Delete Global Field?',
        message: `${names} will no longer be a global field, and the values entered for it will be removed from every ${this.entityLabel.toLowerCase()}. This cannot be undone.`,
        backdropDismiss: false,
        buttons: [
          { text: 'Cancel', role: 'cancel', handler: () => resolve(false) },
          { text: 'Delete', role: 'destructive', handler: () => resolve(true) }
        ]
      });
      await alert.present();
    });
  }

  private Escape(text: string): string {
    return (text || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  Close() {
    this.modalController.dismiss();
  }
}
